import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, User, Youtube, Music2 } from "lucide-react";
import { api, formatDate, imgUrl } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { EpisodeHero } from "@/components/EpisodeHero";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ClubGlobe } from "@/components/ClubGlobe";
import { ShowReviews, REVIEW_BTN } from "@/components/ShowReviews";
import { RelatedArticles } from "@/components/articles/ArticleBody";

const PAGE = 10;

export default function ShowPage() {
  const { slug } = useParams();
  const [show, setShow] = useState(null);
  const [error, setError] = useState(false);
  const [visible, setVisible] = useState(PAGE);

  useEffect(() => {
    setShow(null); setError(false); setVisible(PAGE);
    api.get(`/shows/${slug}`, { params: { limit: 5000 } }).then((r) => setShow(r.data)).catch(() => setError(true));
  }, [slug]);

  return (
    <div className="min-h-screen flex flex-col" data-testid="show-page">
      <Navbar />
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-8 pb-32">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="show-back-link"><ArrowLeft size={16} /> Back to home</Link>
        {error ? (
          <div className="card-surface rounded-3xl p-12 text-center text-slate-400" data-testid="show-not-found">Show not found.</div>
        ) : !show ? (
          <Skeleton className="h-72 rounded-3xl bg-white/5" />
        ) : (
          <>
            <div className="grid lg:grid-cols-[3fr_1fr] gap-8 items-start">
              <div className={`grid ${show.kind === "video" ? "md:grid-cols-[320px_1fr]" : "md:grid-cols-[220px_1fr]"} gap-8 items-start`}>
                <img src={imgUrl(show.cover_url)} alt={show.name} className="aspect-square w-full rounded-2xl object-cover ring-1 ring-white/10 shadow-2xl" data-testid="show-cover" />
                <div>
                  <p className="eyebrow mb-2">{show.kind === "video" && <span className="mr-2 rounded-full bg-rose-500/15 px-2 py-0.5 text-rose-400">Video series</span>}{show.kind === "tiktok" && <span className="mr-2 rounded-full bg-cyan-400/15 px-2 py-0.5 text-cyan-300">TikTok</span>}{show.placements?.length ? show.placements.map((p) => [p.group, p.subgroup].filter(Boolean).join(" · ")).join("  /  ") : show.category}</p>
                  <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none" data-testid="show-title">{show.name}</h1>
                  {show.host && <p className="mt-3 text-slate-300 font-semibold">Hosted by {show.host}</p>}
                  <p className="mt-4 text-slate-400 leading-relaxed max-w-2xl" data-testid="show-description">{show.description}</p>
                  <div className="mt-5 flex flex-wrap gap-3 text-sm">
                    <span className="rounded-full bg-white/5 px-3 py-1 font-mono text-xs text-slate-300" data-testid="show-episode-count">{show.episode_count} {show.kind === "audio" ? "episodes" : "videos"}</span>
                    {show.kind === "tiktok" && show.tiktok_url && <a href={show.tiktok_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300 hover:text-white" data-testid="show-tiktok-link"><Music2 size={12} className="text-cyan-300" /> @{show.tiktok_url.split("@")[1] || "tiktok"}</a>}
                    {show.kind === "video" && show.youtube_playlist_id && <a href={`https://www.youtube.com/playlist?list=${show.youtube_playlist_id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300 hover:text-white" data-testid="show-playlist-link"><Youtube size={12} className="text-rose-500" /> Playlist</a>}
                    {show.website && <a href={show.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-slate-300 hover:text-white"><ExternalLink size={12} /> Website</a>}
                    <Link to={`/review?show=${encodeURIComponent(show.slug)}`} className={`${REVIEW_BTN} !px-4 !py-1.5 !text-xs`} data-testid="show-review-button">Review this show</Link>
                  </div>
                </div>
              </div>

              <aside className="card-surface grain relative overflow-hidden rounded-3xl p-5" data-testid="show-hosts-panel">
                <p className="eyebrow mb-4">{show.hosts?.length > 1 ? "Your hosts" : "Your host"}</p>
                {show.hosts?.length ? (
                  <div className="space-y-5">
                    {show.hosts.map((h, i) => (
                      <Link to={`/hosts/${h.slug}`} key={h.id} className="group flex flex-col items-center text-center" data-testid={`show-host-${i}`}>
                        <div className="relative">
                          <div className="absolute inset-0 rounded-full bg-sky-400/30 blur-xl" />
                          {h.image_url ? <img src={imgUrl(h.image_url)} alt={h.name} className="relative h-28 w-28 rounded-full object-cover ring-2 ring-sky-400/40 transition-transform duration-300 group-hover:scale-105" /> : <div className="relative h-28 w-28 rounded-full bg-[#053671] ring-2 ring-sky-400/40 flex items-center justify-center"><User size={36} className="text-sky-200" /></div>}
                        </div>
                        <p className="mt-3 font-display font-bold text-lg leading-tight group-hover:text-sky-300 transition-colors" data-testid={`show-host-name-${i}`}>{h.name}</p>
                        <p className="text-xs font-mono uppercase tracking-widest text-sky-400 mt-0.5">{h.role}</p>
                        {h.bio && <p className="mt-2 text-xs text-slate-400 leading-relaxed">{h.bio}</p>}
                        <span className="mt-2 text-xs text-slate-500 group-hover:text-sky-300">View profile →</span>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-center" data-testid="show-host-fallback">
                    <div className="h-28 w-28 rounded-full bg-[#053671] ring-2 ring-white/10 flex items-center justify-center"><User size={36} className="text-sky-200" /></div>
                    <p className="mt-3 font-display font-bold text-lg leading-tight">{show.host || "Host coming soon"}</p>
                    <p className="text-xs font-mono uppercase tracking-widest text-sky-400 mt-0.5">Host</p>
                  </div>
                )}
              </aside>
            </div>

            {show.club_map && <ClubGlobe show={show} />}
            <div className="-mx-5 sm:-mx-8 mt-10"><ShowReviews show={show} /></div>
            <RelatedArticles showSlug={show.slug} />

            <h2 className="mt-14 mb-5 font-display text-2xl font-extrabold">Episodes</h2>
            <div className="space-y-5" data-testid="show-episode-list">
              {show.episodes.length === 0 && <p className="text-slate-400" data-testid="show-no-episodes">{show.kind === "tiktok" ? "No TikToks yet — new clips appear here as they're added." : "No episodes yet — check back soon."}</p>}
              {show.episodes.slice(0, visible).map((ep, i) => (
                <EpisodeHero key={ep.id} episode={{ ...ep, show }} eyebrow={i === 0 ? "Latest episode" : formatDate(ep.published_at)} showBadge={false} testId={`show-episode-${i}`} index={Math.min(i, 6)} />
              ))}
            </div>
            {visible < show.episodes.length && (
              <div className="mt-8 flex justify-center">
                <Button onClick={() => setVisible((v) => v + PAGE)} variant="outline" className="rounded-full border-white/15 bg-transparent hover:bg-white/10 px-8" data-testid="show-load-more">Load more episodes</Button>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
